import { FieldArray, Formik, getIn, useField, useFormikContext } from 'formik'
import { Form } from 'components/Wizards/Form/Form'
import { Heading } from 'blaze-ui/Heading/Heading'
import Styles from 'components/Wizards/BlogPostWizard/BlogPostWizard.module.scss'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Button } from 'blaze-ui/Button'
import { ArrowRight } from 'almanac-ui/icons'
import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { LinkwithIds, Values } from './Types'
import { Text } from 'blaze-ui/Text/Text'
import { LinkExternal, Trash2 } from 'blaze-ui/icons'
import { IconButton } from 'components/IconButton/IconButton'
import { v4 as uuidv4 } from 'uuid'
import { useLinks } from './LinksProvider'
import { Plus1 } from 'blaze-ui/icons/24'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { Pressable } from '@react-aria/interactions'
import { useRootStore } from 'stores/RootStore'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { TextDrawer } from 'components/TextDrawer/TextDrawer'
import { LinkGenerationError } from './LinkGenerationError'
import { LinkGenerationLoading } from './LinkGenerationLoading'

interface Props extends Pick<StepProps, 'onBack'> {}

interface FormValues extends Values {
  internal_links: LinkwithIds[]
  external_links: LinkwithIds[]
  excluded_links: LinkwithIds[]
  generate_external_links: boolean
}

type FormStatus = {
  internal_links?: Record<string, string>
  external_links?: Record<string, string>
  excluded_links?: Record<string, string>
}

interface LinkInputSectionProps {
  links: LinkwithIds[]
  setLinks: (links: LinkwithIds[]) => void
  errors: any
  touched: any
  status: FormStatus | undefined
  setStatus: (status: FormStatus | undefined) => void
  values: FormValues
  setFieldTouched: (field: string, isTouched?: boolean) => void
  placeholder: string
}

const INTERNAL_TOOLTIP =
  'An internal link is a clickable link that takes you to another page on the same website. It helps people find more content and makes it easier for search engines to understand your site.'
const EXTERNAL_TOOLTIP =
  'An external link is a clickable link that takes you to a different website. It helps share useful information and shows search engines that your content is well-connected to other sources.'

const LinkInputSection = ({
  links,
  setLinks,
  errors,
  touched,
  status,
  setStatus,
  values,
  setFieldTouched,
  placeholder,
}: LinkInputSectionProps) => {
  const name = 'external_links'
  return (
    <FieldArray name={name}>
      {({ push, remove }) => (
        <div className={Styles.linkInputsContainer}>
          {links.map((link, i) => {
            const { id } = link
            const fieldError = getIn(errors, `${name}[${i}].url`)
            const fieldTouched = getIn(touched, `${name}[${i}].url`)
            const fieldStatus = status?.internal_links?.[id]
            const fieldAdded = fieldStatus === 'added'
            const isEmpty = values[name][i].url === ''

            const addField = () => {
              push({ id: uuidv4(), url: '' })
              setStatus({
                ...status,
                internal_links: {
                  ...(status?.internal_links || {}),
                  [id]: 'added',
                },
              })
              requestAnimationFrame(() => {
                const inputField: HTMLInputElement | null = document.querySelector(
                  `input[name="${name}[${links.length}].url"]`,
                )
                inputField?.focus()
              })
            }

            return (
              <div key={link.id} className={Styles.linkInputContainer}>
                <div className={Styles.linkInput} title={link.url}>
                  <Input
                    type="url"
                    isValid={isEmpty || (fieldTouched && !fieldError)}
                    errorMessage={!isEmpty && fieldError}
                    name={`${name}[${i}].url`}
                    placeholder={placeholder}
                    onChange={(e) => {
                      const newLinks = [...links]
                      newLinks[i] = { ...newLinks[i], url: e.target.value }
                      setLinks(newLinks)
                      setFieldTouched(`${name}.${i}.url`, true)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && fieldTouched && !isEmpty && !fieldError && !fieldAdded) {
                        e.preventDefault()
                        addField()
                      }
                    }}
                  />
                </div>
                <div>
                  <div className={Styles.linkActions}>
                    {!fieldAdded && (
                      <Button
                        isDisabled={!fieldTouched || isEmpty || fieldError}
                        onPress={() => addField()}
                        variant="secondary"
                      >
                        {'Add'}
                      </Button>
                    )}
                    {fieldAdded && (
                      <IconButton
                        onPress={() => {
                          remove(i)
                        }}
                      >
                        <Trash2 />
                      </IconButton>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </FieldArray>
  )
}

const SelectLinks = ({ onBack }: Props) => {
  const {
    generatedLinks,
    generateLinks,
    setGeneratedLinks,
    removeLink,
    isLoading,
    abortGeneration,
    error: errorGenerating,
  } = useLinks()
  const { values, submitForm, errors, touched, status, setFieldTouched, setStatus } = useFormikContext<FormValues>()
  const [{ value: externalLinks }, , { setValue: setExternalLinks }] = useField<LinkwithIds[]>('external_links')
  const { openDrawer } = useRootStore().drawersStore
  const { isMobile, isTabletPortrait } = useResponsiveDesign()

  return (
    <Form.Root>
      <Form.Section>
        <Form.SectionContent>
          <Form.Fields className={Styles.fieldsWrapper}>
            <div className={Styles.linkSectionHeadingContainer}>
              <Heading level={4}>What links would you like to include?</Heading>
              <Text variant="secondary" color="var(--dark-80)">
                <span>Boost your SEO by incorporating a mix of </span>
                {/* Internal tooltip code */}
                <ResponsiveDesign.Desktop>
                  <Tooltip mouseEnterDelay={0} label={INTERNAL_TOOLTIP}>
                    <span className={Styles.tooltipped}>internal</span>
                  </Tooltip>
                </ResponsiveDesign.Desktop>
                <ResponsiveDesign.Mobile>
                  <Pressable
                    onPress={() => openDrawer(TextDrawer, { text: INTERNAL_TOOLTIP, title: 'Internal Links' })}
                  >
                    <span className={Styles.tooltipped}>internal</span>
                  </Pressable>
                </ResponsiveDesign.Mobile>
                <span> and </span>
                {/* External tooltip code */}
                <ResponsiveDesign.Desktop>
                  <Tooltip mouseEnterDelay={0} label={EXTERNAL_TOOLTIP}>
                    <span className={Styles.tooltipped}>external</span>
                  </Tooltip>
                </ResponsiveDesign.Desktop>
                <ResponsiveDesign.Mobile>
                  <Pressable
                    onPress={() => openDrawer(TextDrawer, { text: EXTERNAL_TOOLTIP, title: 'External Links' })}
                  >
                    <span className={Styles.tooltipped}>external</span>
                  </Pressable>
                </ResponsiveDesign.Mobile>
                <span> links.</span>
              </Text>
            </div>

            <div className={Styles.linkSection}>
              <Text variant="secondary" color="var(--dark-100)">
                For a short blog post, aim for around 5 links. Use our recommendations below, or add, generate, or
                remove links that don’t fit.
              </Text>
              <div className={Styles.linkInputsContainer}>
                {generatedLinks.map((link) => {
                  const { id, url } = link
                  return (
                    <div key={id} className={Styles.linkInputContainer} title={url}>
                      <Input type="url" disabled name={`generated_urls_${id}`} value={url} />
                      <div className={Styles.linkActions}>
                        <a href={url} target="_blank" rel="noreferrer noopener">
                          <IconButton>
                            <LinkExternal />
                          </IconButton>
                        </a>

                        <IconButton
                          onPress={() => {
                            removeLink(link)
                          }}
                        >
                          <Trash2 />
                        </IconButton>
                      </div>
                    </div>
                  )
                })}
                {isLoading && <LinkGenerationLoading />}
                {errorGenerating && <LinkGenerationError retry={() => generateLinks(values)} />}
              </div>
              {!errorGenerating && (
                <div>
                  <Button
                    variant="secondary"
                    isDisabled={isLoading}
                    frontIcon={Plus1}
                    onPress={() => {
                      generateLinks(values)
                    }}
                  >
                    {generatedLinks.length ? 'Generate 5 More' : 'Generate 5 Links'}
                  </Button>
                </div>
              )}
            </div>

            <div className={Styles.linkSection}>
              <LinkInputSection
                links={externalLinks}
                setLinks={setExternalLinks}
                errors={errors}
                touched={touched}
                status={status}
                setStatus={setStatus}
                values={values}
                setFieldTouched={setFieldTouched}
                placeholder="Paste or type a link here"
              />
            </div>
          </Form.Fields>
        </Form.SectionContent>
      </Form.Section>
      <Form.Actions isSticky>
        {!isTabletPortrait && (
          <Form.PreviousButton
            onPress={() => {
              abortGeneration()
              onBack?.()
            }}
          >
            Previous Step
          </Form.PreviousButton>
        )}

        <Form.PrimaryActions>
          <Button
            variant="tertiary"
            size="lg"
            onPress={() => {
              abortGeneration()
              setGeneratedLinks([])
              submitForm()
            }}
            fullWidth={isMobile}
          >
            Skip
          </Button>
          <Button size="lg" isDisabled={isLoading} endIcon={ArrowRight} onPress={submitForm} fullWidth={isMobile}>
            Select Images
          </Button>
        </Form.PrimaryActions>
      </Form.Actions>
    </Form.Root>
  )
}

const SelectLinksContainer = multiStep(({ schema, onSubmit, onBack, initialValues }: StepProps) => {
  const { generatedLinks } = useLinks()
  return (
    <Formik
      enableReinitialize
      validationSchema={schema}
      onSubmit={(values) => {
        return onSubmit({ ...values, generated_links: generatedLinks })
      }}
      initialValues={schema.cast({ ...schema.getDefault(), ...initialValues }, { assert: false })}
    >
      <SelectLinks onBack={onBack} />
    </Formik>
  )
})

export { SelectLinksContainer as SelectLinks }
